import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GoArrowLeft, GoArrowRight } from "react-icons/go";

const CARD_SIZE_LG = 365;
const CARD_SIZE_SM = 290;

const BORDER_SIZE = 2;
const CORNER_CLIP = 50;
const CORNER_LINE_LEN = Math.sqrt(
  CORNER_CLIP * CORNER_CLIP + CORNER_CLIP * CORNER_CLIP
);

const ROTATE_DEG = 2.5;

const STAGGER = 15;
const CENTER_STAGGER = -65;

/**
 * To allow for responsiveness, you may adjust this SECTION_HEIGHT
 * or turn it into a min-height so content remains contained on smaller screens.
 */
const SECTION_HEIGHT = 600; 

export const StaggerTestimonials = () => {
  const [cardSize, setCardSize] = useState(CARD_SIZE_LG);
  const [testimonials, setTestimonials] = useState(TESTIMONIAL_DATA);

  const handleMove = (position) => {
    const copy = [...testimonials];

    if (position > 0) {
      for (let i = position; i > 0; i--) {
        const firstEl = copy.shift();
        if (!firstEl) return;
        copy.push({ ...firstEl, tempId: Math.random() });
      }
    } else {
      for (let i = position; i < 0; i++) {
        const lastEl = copy.pop();
        if (!lastEl) return;
        copy.unshift({ ...lastEl, tempId: Math.random() });
      }
    }

    setTestimonials(copy);
  };

  useEffect(() => {
    const { matches } = window.matchMedia("(min-width: 640px)");

    if (matches) {
      setCardSize(CARD_SIZE_LG);
    } else {
      setCardSize(CARD_SIZE_SM);
    }

    const handleSetCardSize = () => {
      const { matches } = window.matchMedia("(min-width: 640px)");
      if (matches) {
        setCardSize(CARD_SIZE_LG);
      } else {
        setCardSize(CARD_SIZE_SM);
      }
    };

    window.addEventListener("resize", handleSetCardSize);
    return () => window.removeEventListener("resize", handleSetCardSize);
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden bg-neutral-200"
      style={{
        // You can also use minHeight to allow more natural growth:
        // minHeight: SECTION_HEIGHT,
        height: SECTION_HEIGHT,
      }}
    >
      {testimonials.map((t, idx) => {
        let position = 0;
        if (testimonials.length % 2) {
          position = idx - (testimonials.length + 1) / 2;
        } else {
          position = idx - testimonials.length / 2;
        }

        return (
          <TestimonialCard
            key={t.tempId}
            testimonial={t}
            handleMove={handleMove}
            position={position}
            cardSize={cardSize}
          />
        );
      })}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-8">
        <button
          onClick={() => handleMove(-1)}
          className="grid h-14 w-14 place-content-center text-3xl transition-colors bg-indigo-600 hover:bg-black hover:text-white"
        >
          <GoArrowLeft />
        </button>
        <button
          onClick={() => handleMove(1)}
          className="grid h-14 w-14 place-content-center text-3xl transition-colors bg-indigo-600 hover:bg-black hover:text-white"
        >
          <GoArrowRight />
        </button>
      </div>
    </div>
  );
};

const TestimonialCard = ({ position, testimonial, handleMove, cardSize }) => {
  const isActive = position === 0;

  return (
    <motion.div
      initial={false}
      onClick={() => handleMove(position)}
      className={`
        absolute left-1/2 top-1/2 cursor-pointer border-black 
        transition-colors duration-500 
        overflow-hidden break-words
        flex flex-col justify-between
        p-4 sm:p-6 
        ${isActive ? "z-10 bg-indigo-600" : "z-0 bg-white"}
      `}
      style={{
        borderWidth: BORDER_SIZE,
        clipPath: `polygon(
          ${CORNER_CLIP}px 0%, 
          calc(100% - ${CORNER_CLIP}px) 0%, 
          100% ${CORNER_CLIP}px, 
          100% 100%, 
          calc(100% - ${CORNER_CLIP}px) 100%, 
          ${CORNER_CLIP}px 100%, 
          0 100%, 
          0 0
        )`,
      }}
      animate={{
        width: cardSize,
        height: cardSize,
        x: `calc(-50% + ${position * (cardSize / 1.5)}px)`,
        y: `calc(-50% + ${
          isActive ? CENTER_STAGGER : position % 2 ? STAGGER : -STAGGER
        }px)`,
        rotate: isActive ? 0 : position % 2 ? ROTATE_DEG : -ROTATE_DEG,
        boxShadow: isActive ? "0px 8px 0px 4px black" : "0px 0px 0px 0px black",
      }}
      transition={{
        type: "spring",
        mass: 3,
        stiffness: 400,
        damping: 50,
      }}
    >
      {/* Slanted border segment */}
      <span
        className="absolute block origin-top-right rotate-45 bg-black object-cover"
        style={{
          right: -BORDER_SIZE,
          top: CORNER_CLIP - BORDER_SIZE,
          width: CORNER_LINE_LEN,
          height: BORDER_SIZE,
        }}
      />
      
      {/* Testimonial text */}
      <h3
        className={`text-sm sm:text-base font-medium ${
          isActive ? "text-white" : "text-black"
        }`}
      >
        “{testimonial.testimonial}”
      </h3>

      {/* By-line at bottom */}
      <p
        className={`mt-2 text-xs sm:text-sm italic ${
          isActive ? "text-indigo-200" : "text-neutral-700"
        }`}
      >
        - {testimonial.by}
      </p>
    </motion.div>
  );
};

// Sample testimonial data
const TESTIMONIAL_DATA = [
  {
    tempId: 0,
    testimonial:
      "FuteurAI’s insights helped me understand how business credit works, especially the importance of keeping credit utilization low. After using their tools, I managed to improve my credit score, which allowed me to secure a small business loan to expand our bakery.Thank you for making this process so straightforward!",
    by: "Jessica Ramirez – Ramirez Family Bakery",
  },
  {
    tempId: 1,
    testimonial:
      "Running an IT startup, I always struggled with understanding business credit and how to use it to secure vendor relationships. FuteurAI gave me a clear, step-by-step plan on how to build and monitor my credit. As a result, I secured a critical vendor contract that boosted our ability to scale and take on larger projects. ",
    by: "Michael Thompson – Thompson IT Solutions",
  },
  {
    tempId: 2,
    testimonial:
      "When I first started Amara’s Boutique, I had no idea how vendors viewed my credit. FuteurAI not only helped me track my credit health but also explained how vendors evaluate my creditworthiness. Thanks to their advice, I was able to improve my credit score and negotiate better payment terms with my suppliers.",
    by: "Amara Patel – Amara’s Boutique",
  },
  {
    tempId: 3,
    testimonial:
      "As a small landscaping business owner, I was unsure of how to leverage my business credit to fuel growth. FuteurAI showed me exactly how to manage my credit accounts, including tips on maximizing vendor credit. I’ve since secured a lease on new equipment that has dramatically increased efficiency and the scale of projects we can handle.",
    by: "Robert Klein – Klein’s Landscaping",
  },
  {
    tempId: 4,
    testimonial:
      "I was hesitant to take out a loan to renovate Sunshine Café, but FuteurAI’s platform helped me understand how to strategically use my credit score. Their resources helped me improve my credit profile, which made me feel confident when applying for funding.",
    by: "Linda Park – Sunshine Café",
  },
  {
    tempId: 5,
    testimonial:
      "I knew business credit was important, but I had no clue how to actually build it. FuteurAI’s platform walked me through the entire process. I applied the insights and soon qualified for a lease on high-end photography equipment that I had always dreamed of using. This opened up bigger opportunities for client work, and my business has since expanded.",
    by: "Ethan Johnson – Johnson Photography",
  },
  {
    tempId: 6,
    testimonial:
      "Running a floral business, I had always been cautious about credit, but FuteurAI helped me track my business credit in real-time. This gave me peace of mind, and I was able to negotiate better terms with my vendors. Now I have a more stable cash flow, and I’m confident in my ability to grow as I take on larger contracts.",
    by: "Maria Lopez – Maria’s Floral Creations",
  },
  {
    tempId: 7,
    testimonial:
      "FuteurAI gave me insights into managing my business credit that I never knew existed. After applying their advice, I was able to secure financing to upgrade our tools and equipment. This has helped streamline operations and expand our capacity to take on more clients. Their platform is easy to use and has made a real impact on my business.",
    by: "David Harper – Harper Auto Repair",
  },
  {
    tempId: 8,
    testimonial:
      "Opening a second location for my yoga studio felt like a huge leap, but FuteurAI helped me understand how my business credit could help make it happen. They provided me with the steps I needed to take, and I was able to secure funding to open the new location. I couldn’t be more excited to see our growth, all thanks to the actionable advice from FuteurAI.",
    by: "Emily Wu – Lotus Yoga Studio",
  },
  {
    tempId: 9,
    testimonial:
      "I didn’t know where to start when it came to business credit. FuteurAI’s platform showed me how to monitor my credit in real-time and provided insights that helped me make smarter financial decisions. This led to the growth of my marketing agency and allowed us to scale faster by securing larger contracts with better payment terms.",
    by: "Anthony Rodriguez – AR Marketing Solutions",
  },
  {
    tempId: 10,
    testimonial:
      "Managing vendor relationships has always been tricky, but FuteurAI’s resources taught me how to leverage my business credit to negotiate better terms. As a result, I now have consistent access to inventory at better rates, and my business is growing faster than ever. Their guidance has been invaluable to me.",
    by: "Sophia Chen – Threads by Sophia",
  },
  {
    tempId: 11,
    testimonial:
      "I’ve been in the trucking business for years, but understanding how business credit could help me expand was a game-changer. FuteurAI showed me exactly how to improve my credit score, which helped me secure financing for a new fleet of trucks. This has opened up new opportunities and allowed us to service more clients.",
    by: "James Walker – Walker Freight Lines",
  },
  {
    tempId: 12,
    testimonial:
      "FuteurAI made managing my business credit straightforward. They helped me improve my credit score, which enabled me to negotiate better terms with suppliers. This allowed me to optimize my cash flow, improve restaurant operations, and even offer better deals to customers. Our sales have increased, and so has our customer base.",
    by: "Laura Morales – El Camino Restaurant",
  },
  {
    tempId: 13,
    testimonial:
      "As a craft business owner, managing credit seemed overwhelming. FuteurAI explained how to use my credit strategically, helping me gain access to vendor credit. With better payment terms and a streamlined supply chain, I can focus more on growing my business and less on worrying about credit.",
    by: "Priya Sharma – Priya’s Crafts & Creations",
  },
  {
    tempId: 14,
    testimonial:
      "I had no clue about business credit until I used FuteurAI. Their educational tools showed me how to leverage my credit to grow, and now we’re opening a second office in a new city. The process was easier than I expected, and I feel more confident about taking CodeNation to the next level.",
    by: "Alex Davis – CodeNation",
  },
  {
    tempId: 15,
    testimonial:
      "FuteurAI’s credit monitoring tools helped me stay informed about my financial standing. As a result, I made confident decisions that allowed my cleaning service to expand its client base. Their platform is easy to use and provides real-time updates that keep me ahead of the game.",
    by: "Rachel Kim – Sparkle Cleaners",
  },
  {
    tempId: 16,
    testimonial:
      "I’ve learned so much about managing business credit thanks to FuteurAI. Their platform helped me track the factors that matter most to lenders, allowing me to secure funding to expand my brewery operations. The improvements we’ve made have taken Oregon Brewing Co. to the next level.",
    by: "Chris Nguyen – Oregon Brewing Co.",
  },
  {
    tempId: 17,
    testimonial:
      "FuteurAI’s platform helped me improve my credit, which opened doors to better vendor relationships and new project opportunities. Their insights helped me better manage credit for cash flow optimization, and now I’m able to take on larger, more lucrative projects.",
    by: "Aaron Patel – Patel Construction Co.",
  },
  {
    tempId: 18,
    testimonial:
      "Understanding how credit impacts business growth was eye-opening. FuteurAI’s tools gave me the knowledge I needed to scale my event planning business. I’ve taken on much larger events and been able to attract more clients as a result.",
    by: "Megan Carter – Blissful Events",
  },
  {
    tempId: 19,
    testimonial:
      "FuteurAI’s real-time updates kept me informed about my credit standing, helping me make decisions that allowed me to expand my gym. With their guidance, I was able to secure financing to open a new location, and now we’re offering new classes and services.",
    by: "Steven Brown – Fitness First",
  },
];

